"use client"

import { useState, useMemo } from "react"
import { Plus, Trash2, RotateCcw, Lightbulb } from "lucide-react"
import type { Idioma } from "@/lib/landing/idioma"

interface Ingredient {
  id: number
  nombre: string
  cantidad: number
  unidad: string
}

const T = {
  es: {
    ingredientes: [
      "Tomates maduros",
      "Pepino",
      "Pimiento verde",
      "Ajo",
      "Aceite de oliva virgen extra",
      "Vinagre de Jerez",
      "Pan del día anterior",
      "Sal",
    ],
    unidades: { uds: "uds", cucharadas: "cucharadas", cucharaditas: "cucharaditas" } as Record<string, string>,
    titulo: "Conversor de raciones",
    limpiar: "Limpiar",
    racionesOriginales: "Raciones originales",
    racionesDeseadas: "Raciones deseadas",
    rapido: "Rápido:",
    factor: "Factor de conversión:",
    tituloIngredientes: "Ingredientes",
    ingrediente: "Ingrediente",
    cantidad: "Cantidad",
    unidad: "Unidad",
    nuevaCantidad: "Nueva cantidad",
    eliminar: "Eliminar ingrediente",
    anadir: "Añadir ingrediente",
    consejo: "Consejo",
    textoConsejo:
      "Al multiplicar una receta, recuerda que los tiempos de cocción pueden variar. Si doblas las cantidades, es posible que necesites más tiempo de horno o cocción. Los condimentos (sal, especias) suelen necesitar un ajuste menor que el factor de conversión — prueba y rectifica.",
  },
  en: {
    ingredientes: [
      "Ripe tomatoes",
      "Cucumber",
      "Green pepper",
      "Garlic",
      "Extra virgin olive oil",
      "Sherry vinegar",
      "Day-old bread",
      "Salt",
    ],
    unidades: { uds: "units", cucharadas: "tbsp", cucharaditas: "tsp" } as Record<string, string>,
    titulo: "Portion converter",
    limpiar: "Reset",
    racionesOriginales: "Original portions",
    racionesDeseadas: "Portions needed",
    rapido: "Quick:",
    factor: "Conversion factor:",
    tituloIngredientes: "Ingredients",
    ingrediente: "Ingredient",
    cantidad: "Quantity",
    unidad: "Unit",
    nuevaCantidad: "New quantity",
    eliminar: "Remove ingredient",
    anadir: "Add ingredient",
    consejo: "Tip",
    textoConsejo:
      "When you scale up a recipe, remember that cooking times can change. If you double the quantities, you may need longer in the oven or on the hob. Seasonings (salt, spices) usually need a smaller adjustment than the conversion factor — taste and adjust.",
  },
}

const ingredientesBase: Omit<Ingredient, "nombre">[] = [
  { id: 1, cantidad: 1000, unidad: "g" },
  { id: 2, cantidad: 200, unidad: "g" },
  { id: 3, cantidad: 100, unidad: "g" },
  { id: 4, cantidad: 2, unidad: "uds" },
  { id: 5, cantidad: 100, unidad: "ml" },
  { id: 6, cantidad: 30, unidad: "ml" },
  { id: 7, cantidad: 50, unidad: "g" },
  { id: 8, cantidad: 10, unidad: "g" },
]

const defaultIngredientsDe = (idioma: Idioma): Ingredient[] =>
  ingredientesBase.map((ing, i) => ({ ...ing, nombre: T[idioma].ingredientes[i] }))

const defaultState = {
  racionesOriginales: 4,
  racionesDeseadas: 4,
}

let nextId = 100

export default function RecetaRacionesCalculator({ idioma = "es" }: { idioma?: Idioma }) {
  const t = T[idioma]
  const unidadVisible = (u: string) => t.unidades[u] ?? u
  const [racionesOriginales, setRacionesOriginales] = useState(
    defaultState.racionesOriginales
  )
  const [racionesDeseadas, setRacionesDeseadas] = useState(
    defaultState.racionesDeseadas
  )
  const [ingredients, setIngredients] = useState<Ingredient[]>(() => defaultIngredientsDe(idioma))

  const factor = useMemo(() => {
    return racionesOriginales > 0 ? racionesDeseadas / racionesOriginales : 0
  }, [racionesOriginales, racionesDeseadas])

  const addIngredient = () => {
    setIngredients([
      ...ingredients,
      { id: nextId++, nombre: "", cantidad: 0, unidad: "g" },
    ])
  }

  const removeIngredient = (id: number) => {
    if (ingredients.length > 1) {
      setIngredients(ingredients.filter((i) => i.id !== id))
    }
  }

  const updateIngredient = (id: number, field: keyof Ingredient, value: string | number) => {
    setIngredients(
      ingredients.map((i) => (i.id === id ? { ...i, [field]: value } : i))
    )
  }

  const reset = () => {
    setRacionesOriginales(defaultState.racionesOriginales)
    setRacionesDeseadas(defaultState.racionesDeseadas)
    setIngredients(defaultIngredientsDe(idioma))
  }

  const setMultiplier = (mult: number) => {
    setRacionesDeseadas(racionesOriginales * mult)
  }

  const formatQuantity = (qty: number) => {
    if (qty === 0) return "0"
    if (idioma === "en") return qty.toLocaleString("en-GB", { maximumFractionDigits: Number.isInteger(qty) ? 0 : 1 })
    if (Number.isInteger(qty)) return qty.toString()
    return qty % 1 === 0 ? qty.toFixed(0) : qty.toFixed(1)
  }

  const inputClass =
    "w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent transition-shadow"

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden">
      <div className="p-6 md:p-8 space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-zinc-900">{t.titulo}</h2>
          <button
            onClick={reset}
            className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            {t.limpiar}
          </button>
        </div>

        {/* Raciones inputs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1.5">
              {t.racionesOriginales}
            </label>
            <input
              type="number"
              value={racionesOriginales || ""}
              onChange={(e) =>
                setRacionesOriginales(parseInt(e.target.value) || 0)
              }
              placeholder="4"
              min="1"
              step="1"
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1.5">
              {t.racionesDeseadas}
            </label>
            <input
              type="number"
              value={racionesDeseadas || ""}
              onChange={(e) =>
                setRacionesDeseadas(parseInt(e.target.value) || 0)
              }
              placeholder="8"
              min="1"
              step="1"
              className={inputClass}
            />
          </div>
        </div>

        {/* Quick multiplier buttons */}
        <div className="flex flex-wrap gap-2">
          <span className="text-sm text-zinc-500 self-center mr-1">{t.rapido}</span>
          {[2, 3, 5, 10].map((mult) => (
            <button
              key={mult}
              onClick={() => setMultiplier(mult)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                racionesDeseadas === racionesOriginales * mult
                  ? "bg-orange-500 text-white border-orange-500"
                  : "bg-white text-zinc-700 border-zinc-200 hover:border-orange-300 hover:text-orange-600"
              }`}
            >
              ×{mult}
            </button>
          ))}
        </div>

        {/* Factor */}
        <div className="flex items-center justify-center">
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-2xl bg-orange-50">
            <span className="text-sm font-medium text-zinc-600">{t.factor}</span>
            <span className="text-2xl font-bold text-orange-600">
              ×{factor > 0 ? factor.toFixed(2) : "—"}
            </span>
          </div>
        </div>

        {/* Ingredients */}
        <div>
          <label className="block text-sm font-medium text-zinc-700 mb-3">
            {t.tituloIngredientes}
          </label>

          {/* Header row (desktop) */}
          <div className="hidden sm:grid grid-cols-12 gap-2 mb-2 px-1">
            <span className="col-span-4 text-xs text-zinc-500 font-medium">{t.ingrediente}</span>
            <span className="col-span-2 text-xs text-zinc-500 font-medium">{t.cantidad}</span>
            <span className="col-span-2 text-xs text-zinc-500 font-medium">{t.unidad}</span>
            <span className="col-span-3 text-xs text-zinc-500 font-medium">{t.nuevaCantidad}</span>
            <span className="col-span-1" />
          </div>

          <div className="space-y-3">
            {ingredients.map((ing) => {
              const newQty = ing.cantidad * factor
              return (
                <div
                  key={ing.id}
                  className="grid grid-cols-12 gap-2 items-start"
                >
                  <div className="col-span-12 sm:col-span-4">
                    <input
                      type="text"
                      value={ing.nombre}
                      onChange={(e) =>
                        updateIngredient(ing.id, "nombre", e.target.value)
                      }
                      placeholder={t.ingrediente}
                      className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
                    />
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <input
                      type="number"
                      value={ing.cantidad || ""}
                      onChange={(e) =>
                        updateIngredient(
                          ing.id,
                          "cantidad",
                          parseFloat(e.target.value) || 0
                        )
                      }
                      placeholder="0"
                      min="0"
                      step="any"
                      className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
                    />
                  </div>
                  <div className="col-span-3 sm:col-span-2">
                    <select
                      value={ing.unidad}
                      onChange={(e) =>
                        updateIngredient(ing.id, "unidad", e.target.value)
                      }
                      className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
                    >
                      <option value="g">g</option>
                      <option value="kg">kg</option>
                      <option value="ml">ml</option>
                      <option value="L">L</option>
                      <option value="uds">{unidadVisible("uds")}</option>
                      <option value="cucharadas">{unidadVisible("cucharadas")}</option>
                      <option value="cucharaditas">{unidadVisible("cucharaditas")}</option>
                    </select>
                  </div>
                  <div className="col-span-4 sm:col-span-3">
                    <div className="px-3 py-2 rounded-xl bg-orange-50 border border-orange-200 text-sm font-medium text-orange-700">
                      {factor > 0
                        ? `${formatQuantity(newQty)} ${unidadVisible(ing.unidad)}`
                        : "—"}
                    </div>
                  </div>
                  <div className="col-span-1">
                    <button
                      onClick={() => removeIngredient(ing.id)}
                      className="p-2 text-zinc-400 hover:text-red-500 transition-colors"
                      title={t.eliminar}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
          <button
            onClick={addIngredient}
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-orange-600 hover:text-orange-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t.anadir}
          </button>
        </div>

        {/* Tip */}
        <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200">
          <Lightbulb className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-zinc-700">
            <p className="font-medium text-zinc-900 mb-1">{t.consejo}</p>
            <p>{t.textoConsejo}</p>
          </div>
        </div>
      </div>
    </div>
  )
}
